import { useState } from "react";
import api, { formatApiError, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCog, Mail, KeyRound, Loader2 } from "lucide-react";

export default function AccountSettings() {
  const { user, setUser } = useAuth();
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const changeEmail = async () => {
    if (!emailForm.new_email.trim() || !emailForm.current_password) {
      toast.error("Enter your new email and current password");
      return;
    }
    setSavingEmail(true);
    try {
      const { data } = await api.put("/auth/email", {
        new_email: emailForm.new_email.trim(),
        current_password: emailForm.current_password,
      });
      setToken(data.access_token);
      setUser(data.user);
      toast.success("Email address updated");
      setEmailForm({ new_email: "", current_password: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update email");
    } finally {
      setSavingEmail(false);
    }
  };

  const changePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) {
      toast.error("Fill in all password fields");
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSavingPw(true);
    try {
      await api.put("/auth/password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password updated");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update password");
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-6" data-testid="account-settings">
      <div className="flex items-center gap-2">
        <UserCog className="h-5 w-5 text-primary" />
        <h2 className="font-bold text-lg">Account</h2>
      </div>

      {/* Change email */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Mail className="h-4 w-4" /> Email address
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          You&apos;re signed in as <strong className="text-foreground">{user?.email}</strong>. Enter a new email and your current password to change it.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="acct-new-email">New email</Label>
            <Input id="acct-new-email" type="email" value={emailForm.new_email} onChange={(e) => setEmailForm((s) => ({ ...s, new_email: e.target.value }))} placeholder="you@farm.com" data-testid="account-new-email" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="acct-email-pw">Current password</Label>
            <Input id="acct-email-pw" type="password" value={emailForm.current_password} onChange={(e) => setEmailForm((s) => ({ ...s, current_password: e.target.value }))} placeholder="••••••••" data-testid="account-email-password" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={changeEmail} disabled={savingEmail} className="gap-2" data-testid="account-save-email">
            {savingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Update Email
          </Button>
        </div>
      </div>

      <div className="border-t border-border" />

      {/* Change password */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <KeyRound className="h-4 w-4" /> Password
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="acct-cur-pw">Current password</Label>
            <Input id="acct-cur-pw" type="password" value={pwForm.current_password} onChange={(e) => setPwForm((s) => ({ ...s, current_password: e.target.value }))} placeholder="••••••••" data-testid="account-current-password" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="acct-new-pw">New password</Label>
            <Input id="acct-new-pw" type="password" value={pwForm.new_password} onChange={(e) => setPwForm((s) => ({ ...s, new_password: e.target.value }))} placeholder="At least 6 characters" data-testid="account-new-password" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="acct-confirm-pw">Confirm new password</Label>
            <Input id="acct-confirm-pw" type="password" value={pwForm.confirm} onChange={(e) => setPwForm((s) => ({ ...s, confirm: e.target.value }))} placeholder="Re-enter new password" data-testid="account-confirm-password" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={changePassword} disabled={savingPw} className="gap-2" data-testid="account-save-password">
            {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Update Password
          </Button>
        </div>
      </div>
    </div>
  );
}
